"use client";

import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { NodeIconTile } from "@/components/ui/node-icon-tile";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { NAVIGATION_LINKS } from "@/config/constants";
import { NODE_COLOR_ACTION, NODE_COLOR_TRIGGER } from "@/constants/nodeColors";
import { useActions, useTriggers } from "@/hooks/useActionTriggerDefinitions";
import { useSearch, type SearchResult, type SearchResultType } from "@/hooks/useSearch";
import { getTemplateUrl } from "@/lib/templates/templateUrl";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { useRouter } from "next/navigation";
import { IconWorkflowOutline18 } from "nucleo-ui-outline-18";
import { useCallback, useEffect, useMemo, useState } from "react";
interface CommandMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
const typeLabels: Record<SearchResultType, string> = {
  template: "Templates",
  action: "Actions",
  trigger: "Triggers",
  connector_def: "Connectors",
  workflow: "Workflows"
};
export function CommandMenu({
  open,
  onOpenChange
}: CommandMenuProps) {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const {
    results: searchResults,
    isLoading: isSearching,
    shouldUseApiSearch
  } = useSearch(searchQuery, {
    types: ["template", "action", "trigger", "workflow"],
    limit: 10,
    enabled: open
  });

  // Get definitions to lookup connector info
  const {
    triggers
  } = useTriggers();
  const {
    actions
  } = useActions();
  const connectorTypes = useConnectorsStore(state => state.connectorTypes);

  // Create maps for quick lookup
  const connectorTypesByType = useMemo(() => {
    const map = new Map<string, (typeof connectorTypes)[number]>();
    connectorTypes.forEach(ct => map.set(ct.type, ct));
    return map;
  }, [connectorTypes]);
  const definitionConnectorMap = useMemo(() => {
    const map_0: Record<string, {
      type: string;
      logoUrl?: string;
    }> = {};
    [...triggers, ...actions].forEach(def => {
      if (def.connector?.type) {
        const connectorType = connectorTypesByType.get(def.connector.type);
        map_0[def.type] = {
          type: def.connector.type,
          logoUrl: connectorType?.logoUrl
        };
      }
    });
    return map_0;
  }, [triggers, actions, connectorTypesByType]);
  const groupedResults = useMemo(() => {
    if (!shouldUseApiSearch || searchResults.length === 0) return {};
    const groups: Partial<Record<SearchResultType, SearchResult[]>> = {};
    searchResults.forEach(result => {
      if (!groups[result.type]) {
        groups[result.type] = [];
      }
      groups[result.type]!.push(result);
    });
    return groups;
  }, [shouldUseApiSearch, searchResults]);
  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen) {
      setSearchQuery("");
    }
    onOpenChange(newOpen);
  }, [onOpenChange]);
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleOpenChange(!open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, handleOpenChange]);
  const handleSelect = useCallback((href: string) => {
    handleOpenChange(false);
    router.push(href);
  }, [handleOpenChange, router]);
  const handleResultSelect = useCallback((result_0: SearchResult) => {
    handleOpenChange(false);
    switch (result_0.type) {
      case "template":
        router.push(getTemplateUrl(result_0));
        break;
      case "workflow":
        router.push(`/workflows/edit?id=${result_0.id}`);
        break;
      case "action":
      case "trigger":
        router.push(`/workflows/edit`);
        break;
      default:
        break;
    }
  }, [handleOpenChange, router]);
  const hasSearchResults = shouldUseApiSearch && Object.keys(groupedResults).length > 0;
  const showStaticContent = !shouldUseApiSearch || searchQuery.length < 2;
  return <CommandDialog open={open} onOpenChange={handleOpenChange}>
      <CommandInput placeholder="Search templates, actions, triggers, workflows..." value={searchQuery} onValueChange={setSearchQuery} />
      <CommandList>
        {isSearching && <div className="body-sm flex items-center justify-center py-6">
            <PixelLoader size="sm" className="mr-2" />
            Searching...
          </div>}

        {!isSearching && shouldUseApiSearch && searchResults.length === 0 && searchQuery.length >= 2 && <CommandEmpty>No results found for &quot;{searchQuery}&quot;</CommandEmpty>}

        {hasSearchResults && <>
            {(Object.entries(groupedResults) as [SearchResultType, SearchResult[]][]).map(([type, results], index) => {
          return <div key={type}>
                  {index > 0 && <CommandSeparator />}
                  <CommandGroup heading={typeLabels[type]}>
                    {results.map(result_1 => <CommandItem key={`${result_1.type}-${result_1.id}`} value={`${result_1.type} ${result_1.name} ${result_1.description || ""}`} onSelect={() => handleResultSelect(result_1)} className="group cursor-pointer">
                        {result_1.type === "workflow" ? <div className="mr-2 flex h-8 w-8 shrink-0 items-center justify-center">
                            <IconWorkflowOutline18 className="text-ds-text-muted h-5 w-5" />
                          </div> : result_1.type === "template" ? <NodeIconTile size="xs" id={result_1.id} subject={result_1.name} context={result_1.description || ""} connector={definitionConnectorMap[result_1.id]} template="b3os-app-flat" alt={result_1.name} wrapClassName="mr-2" /> : <NodeIconTile size="xs" id={result_1.id} subject={result_1.name} context={result_1.description || ""} connector={definitionConnectorMap[result_1.id]} color={result_1.type === "action" ? NODE_COLOR_ACTION : result_1.type === "trigger" ? NODE_COLOR_TRIGGER : undefined} template="b3os-app-flat" alt={result_1.name} wrapClassName="mr-2" />}
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate font-medium group-hover:text-blue-600 dark:group-hover:text-blue-500">
                            {result_1.name}
                          </span>
                          {result_1.description && <span className="text-ds-text-muted truncate text-xs group-hover:text-zinc-800 dark:group-hover:text-zinc-200">
                              {result_1.description}
                            </span>}
                        </div>
                      </CommandItem>)}
                  </CommandGroup>
                </div>;
        })}
            <CommandSeparator />
          </>}

        {showStaticContent && <>
            <CommandGroup heading="Navigation">
              {NAVIGATION_LINKS.map(link => {
            const Icon = link.icon;
            return <CommandItem key={link.href} value={link.label} onSelect={() => handleSelect(link.href)} className="cursor-pointer">
                    <Icon className="mr-2 h-4 w-4" />
                    <span>{link.label}</span>
                  </CommandItem>;
          })}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Quick Actions">
              <CommandItem value="Create Workflow" onSelect={() => handleSelect("/workflows/edit")} className="cursor-pointer">
                <span className="mr-2">✨</span>
                <span>Create New Workflow</span>
              </CommandItem>
              <CommandItem value="View History" onSelect={() => handleSelect("/workflows/history")} className="cursor-pointer">
                <span className="mr-2">📜</span>
                <span>View Workflow History</span>
              </CommandItem>
            </CommandGroup>
          </>}
      </CommandList>
    </CommandDialog>;
}